import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  addMinutes,
  dayMinutes,
  fmtDate,
  fmtTime,
  isToday,
  startOfStockholmDay,
  startOfMonth,
  startOfWeek,
  stockholmParts,
  toDateInput,
} from '../time.ts';
import type { Busy, Room } from '../types.ts';

const HW = 72; // px per hour, day view
const PX = 44; // px per hour, week view
const OPEN_AT_HOUR = 7;

export interface SlotPick {
  roomId: string;
  start: Date;
}

function evClass(b: Busy) {
  return b.kind === 'block' ? 'blk' : b.mine || b.title ? 'own' : 'busy';
}

function EvLabel({ b }: { b: Busy }) {
  const { t } = useTranslation();
  if (b.kind === 'block') return <span>{b.reason || t('cal.blocked')}</span>;
  if (b.title !== null || b.mine) {
    return (
      <>
        {b.title || t('cal.mine')}
        {b.person ? ` · ${b.person}` : ''}
      </>
    );
  }
  return <>{t('cal.busy')}</>;
}

/** Where on the time axis a click landed, rounded down to the quarter hour. */
function pickTime(dayStart: Date, offsetPx: number, pxPerHour: number) {
  const minutes = Math.max(0, Math.floor(((offsetPx / pxPerHour) * 60) / 15) * 15);
  return addMinutes(dayStart, minutes);
}

export function DayView({
  day,
  rooms,
  busy,
  onPick,
  onOpen,
}: {
  day: Date;
  rooms: Room[];
  busy: Busy[];
  onPick: (p: SlotPick) => void;
  onOpen: (b: Busy) => void;
}) {
  const { t } = useTranslation();
  const scroller = useRef<HTMLDivElement>(null);
  const minutes = dayMinutes(day);
  const W = (minutes / 60) * HW;
  const dayEnd = startOfStockholmDay(day, 1);
  const now = new Date();
  const today = isToday(day);
  const x = (d: Date) => ((d.getTime() - day.getTime()) / 3_600_000) * HW;

  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const target = today ? Math.max(0, x(now) - HW * 1.5) : OPEN_AT_HOUR * HW;
    el.scrollLeft = target;
  }, [day.getTime()]);

  const hours: Date[] = [];
  for (let h = day; h < dayEnd; h = addMinutes(h, 60)) hours.push(h);
  const floors = [...new Set(rooms.map((r) => r.floor))].sort((a, b) => a - b);

  return (
    // Focusable so keyboard users can scroll through the day with the arrow keys.
    <div
      className="tl"
      ref={scroller}
      tabIndex={0}
      role="region"
      aria-label={fmtDate(day, { weekday: 'long', day: 'numeric', month: 'long' })}
    >
      <div style={{ minWidth: 112 + W }}>
        <div className="tr th" aria-hidden="true">
          <div className="rn" />
          <div className="tc" style={{ width: W }}>
            {hours.map((h) => (
              <span key={h.getTime()} style={{ left: x(h) + 4 }}>
                {fmtTime(h).slice(0, 2)}
              </span>
            ))}
          </div>
        </div>
        {floors.map((f) => (
          <div key={f} role="group" aria-label={`${t('common.floor')} ${f}`}>
            <div className="fl">
              <span>
                {t('common.floor')} {f}
              </span>
            </div>
            {rooms
              .filter((r) => r.floor === f)
              .map((r) => {
                const rows = busy.filter(
                  (b) => b.roomId === r.id && new Date(b.start) < dayEnd && new Date(b.end) > day,
                );
                const busyNow = rows.some((b) => new Date(b.start) <= now && new Date(b.end) > now);
                return (
                  <div className="tr" key={r.id}>
                    <div className="rn">
                      <span>
                        {today && (
                          <i
                            className={busyNow ? 'b' : ''}
                            title={busyNow ? t('cal.busyNow') : t('cal.freeNow')}
                          />
                        )}
                        {r.name}
                      </span>
                      <small>
                        {r.capacity} {t('common.people')}
                      </small>
                    </div>
                    <div
                      className="tc"
                      style={{ width: W }}
                      onClick={(e) => {
                        if (e.target !== e.currentTarget) return;
                        const left = e.clientX - e.currentTarget.getBoundingClientRect().left;
                        onPick({ roomId: r.id, start: pickTime(day, left, HW) });
                      }}
                    >
                      {today && now > day && (
                        <div className="past" style={{ width: Math.min(W, x(now)) }} />
                      )}
                      {rows.map((b) => {
                        const s = new Date(Math.max(new Date(b.start).getTime(), day.getTime()));
                        const e = new Date(Math.min(new Date(b.end).getTime(), dayEnd.getTime()));
                        const style = { left: x(s), width: Math.max(4, x(e) - x(s)) };
                        const label = (
                          <>
                            <b>{fmtTime(b.start)}</b>&nbsp;
                            <EvLabel b={b} />
                          </>
                        );
                        return b.bookingId ? (
                          <button
                            key={`${b.bookingId}`}
                            type="button"
                            className={`ev ${evClass(b)}`}
                            style={style}
                            onClick={() => onOpen(b)}
                          >
                            {label}
                          </button>
                        ) : (
                          <div
                            key={`${b.kind}-${b.start}`}
                            className={`ev ${evClass(b)}`}
                            style={style}
                          >
                            {label}
                          </div>
                        );
                      })}
                      {today && now > day && now < dayEnd && (
                        <div className="now" style={{ left: x(now) }} />
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ))}
      </div>
    </div>
  );
}

export function WeekView({
  weekStart,
  room,
  busy,
  onPick,
  onOpen,
}: {
  weekStart: Date;
  room: Room;
  busy: Busy[];
  onPick: (p: SlotPick) => void;
  onOpen: (b: Busy) => void;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  const days = Array.from({ length: 7 }, (_, i) => startOfStockholmDay(weekStart, i));
  useEffect(() => {
    if (scroller.current) scroller.current.scrollTop = OPEN_AT_HOUR * PX;
  }, [weekStart.getTime()]);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  return (
    <div
      className="gw"
      ref={scroller}
      tabIndex={0}
      role="region"
      aria-label={`${room.name}, ${fmtDate(weekStart, { day: 'numeric', month: 'long' })}`}
    >
      <div className="g">
        <div className="hc" aria-hidden="true">
          <div className="gh" />
          {hours.map((h) => (
            <div className="hr" key={h}>
              {String(h).padStart(2, '0')}
            </div>
          ))}
        </div>
        {days.map((d) => {
          const end = startOfStockholmDay(d, 1);
          const minutes = dayMinutes(d);
          const y = (t: Date) => ((t.getTime() - d.getTime()) / 3_600_000) * PX;
          const rows = busy.filter(
            (b) => b.roomId === room.id && new Date(b.start) < end && new Date(b.end) > d,
          );
          return (
            <div className="col" key={d.getTime()}>
              <div className={`gh${isToday(d) ? ' today' : ''}`}>
                {fmtDate(d, { weekday: 'short', day: 'numeric' })}
              </div>
              <div
                className="cb"
                style={{ height: (minutes / 60) * PX }}
                onClick={(e) => {
                  if (e.target !== e.currentTarget) return;
                  const top = e.clientY - e.currentTarget.getBoundingClientRect().top;
                  onPick({ roomId: room.id, start: pickTime(d, top, PX) });
                }}
              >
                {rows.map((b) => {
                  const s = new Date(Math.max(new Date(b.start).getTime(), d.getTime()));
                  const e = new Date(Math.min(new Date(b.end).getTime(), end.getTime()));
                  const style = { top: y(s), height: Math.max(6, y(e) - y(s)) };
                  const label = (
                    <>
                      <b>
                        {fmtTime(s)}–{fmtTime(e)}
                      </b>{' '}
                      <EvLabel b={b} />
                    </>
                  );
                  return b.bookingId ? (
                    <button
                      key={b.bookingId}
                      type="button"
                      className={`ev ${evClass(b)}`}
                      style={style}
                      onClick={() => onOpen(b)}
                    >
                      {label}
                    </button>
                  ) : (
                    <div key={`${b.kind}-${b.start}`} className={`ev ${evClass(b)}`} style={style}>
                      {label}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MonthView({
  month,
  room,
  busy,
  onDay,
}: {
  month: Date;
  room: Room;
  busy: Busy[];
  onDay: (d: Date) => void;
}) {
  const { t } = useTranslation();
  const first = startOfMonth(month);
  const gridStart = startOfWeek(first);
  const thisMonth = stockholmParts(first).month;
  const days = Array.from({ length: 42 }, (_, i) => startOfStockholmDay(gridStart, i));
  return (
    <div className="mg">
      {days.slice(0, 7).map((d) => (
        <div className="w" key={`w${d.getTime()}`} aria-hidden="true">
          {fmtDate(d, { weekday: 'short' })}
        </div>
      ))}
      {days.map((d) => {
        const end = startOfStockholmDay(d, 1);
        const n = busy.filter(
          (b) =>
            b.kind === 'booking' &&
            b.roomId === room.id &&
            new Date(b.start) < end &&
            new Date(b.end) > d,
        ).length;
        const label = `${fmtDate(d, { weekday: 'long', day: 'numeric', month: 'long' })}${n ? `, ${t('cal.bookings', { count: n })}` : ''}`;
        return (
          <button
            key={d.getTime()}
            type="button"
            className={`${stockholmParts(d).month !== thisMonth ? 'out ' : ''}${isToday(d) ? 'td' : ''}`}
            aria-label={label}
            onClick={() => onDay(d)}
          >
            {stockholmParts(d).day}
            {n > 0 && <i aria-hidden="true">{n}</i>}
          </button>
        );
      })}
    </div>
  );
}

export { toDateInput };
